import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Space, Typography } from 'antd';
import { authFetch } from '../../utils/api';
import { tierPolicyStatusPath, validateTierPolicyStatus } from './policyUpdates';
import type { TierPolicyStatus, TierSavedChange } from './policyUpdates';

export function PolicyUpdateNotice({ change, autoPoll = true }: { change: TierSavedChange; autoPoll?: boolean }) {
  const [status, setStatus] = useState<TierPolicyStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [revision, setRevision] = useState(0);
  const lastManualRevision = useRef(0);
  const update = change.update;
  useEffect(() => {
    if (!update?.changeId) return;
    setChecking(false);
    const manualRequested = revision !== lastManualRevision.current;
    lastManualRevision.current = revision;
    if (!autoPoll && !manualRequested) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    let disposed = false;
    setError(null);
    const check = async () => {
      attempts += 1;
      setChecking(true);
      timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await authFetch(tierPolicyStatusPath(change.tierId, change.brand, update.changeId!), { signal: controller.signal });
        const result = await response.json();
        if (controller.signal.aborted) return;
        if (!response.ok || result.code !== 0) throw new Error(result.msg || '無法確認節點套用進度');
        const data = validateTierPolicyStatus(result.data, change);
        setStatus(data); setError(null);
        if (autoPoll && (data.state === 'PENDING' || data.state === 'APPLYING')) {
          if (attempts < 40) timer = setTimeout(check, 2000);
          else setError('仍在套用中，已停止自動查詢。可稍後再次查詢，原始儲存請求不會重新送出。');
        }
      } catch (cause) {
        if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : '無法確認套用進度');
      } finally {
        clearTimeout(timeout);
        if (!controller.signal.aborted) setChecking(false);
      }
    };
    void check();
    controller.signal.addEventListener('abort', () => {
      if (!disposed) { setChecking(false); setError('查詢逾時，尚未確認套用完成；可再次查詢原紀錄。'); }
    });
    return () => { disposed = true; controller.abort(); clearTimeout(timer); clearTimeout(timeout); };
  }, [change, update, autoPoll, revision]);
  const current = status?.state ?? update?.enforcementState;
  const applied = current === 'APPLIED' && (status !== null || update?.changeId === null);
  const failed = current === 'FAILED';
  const scope = status?.executionScope;
  return <Alert
    data-testid="tier-policy-progress"
    data-change-id={update?.changeId ?? 'unknown'}
    data-policy-revision={update?.policyRevision}
    showIcon
    type={failed ? 'error' : applied ? 'success' : 'info'}
    style={{ marginBottom: 12 }}
    message={`${change.name}：${update?.changeId === null ? '已儲存，沒有政策變更' : applied ? scope ? '已完成核定節點套用' : '已完成節點套用' : failed ? '已儲存，節點套用失敗' : '已儲存，正在確認節點套用'}`}
    description={<Space direction="vertical" size={4}>
      <span>{update?.changeId === null ? '本次只更新顯示資料或沒有政策變更。' : applied ? '伺服器已確認此次政策的節點套用證據。' : '儲存成功代表資料已保存；節點套用尚未確認完成，會員可能需要重新連線。'}</span>
      {scope && <Typography.Text>本次核定節點：{scope.nodeEnforcement.map(node => node.nodeId).join('、') || '無適用節點'}。歷史節點與過去主機仍未驗證。</Typography.Text>}
      {update?.changeId && <Typography.Text type="secondary">政策版本 {update.policyRevision} · 套用紀錄 {update.changeId}</Typography.Text>}
      {!autoPoll && !applied && <Typography.Text type="warning">此紀錄仍保留；為限制同時查詢數量，已暫停自動查詢，可手動查詢原紀錄。</Typography.Text>}
      {status && <span>待處理 {status.targets.pending + status.targets.sent}，已確認 {status.targets.acked + status.targets.leaseExpired}，失敗 {status.targets.failed}</span>}
      {error && <Typography.Text type="warning">{error}</Typography.Text>}
      {failed && <Typography.Text type="danger">節點尚未全部套用，請保留這筆紀錄並查核處理狀態。</Typography.Text>}
      {!applied && update?.changeId && <Button size="small" loading={checking} onClick={() => setRevision(value => value + 1)}>再次查詢套用進度</Button>}
    </Space>}
  />;
}
